import React, { useState } from 'react';
import { Alert, Button, Card, Form, Tab, Table, Tabs } from 'react-bootstrap';
import TagsInput from 'react-tagsinput';
import {
  getPresignedFile,
  searchFilesByDesc,
  uploadFile
} from 'services/files/fileElvisService';

const defaultUploadForm = {
  file: null,
  description: '',
  keywords: [],
  active: true
};

const extractPresignedUrl = payload => {
  if (typeof payload === 'string') return payload;
  if (!payload || typeof payload !== 'object') return '';
  return (
    payload.url ||
    payload.presignedUrl ||
    payload.preSignedUrl ||
    payload?.data?.url ||
    ''
  );
};

const normalizeSearchResults = payload => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (payload && typeof payload === 'object') return [payload];
  return [];
};

const getFileDisplayName = file =>
  file?.name ||
  file?.fileName ||
  file?.originalFileName ||
  file?.originalName ||
  (file?.id !== undefined ? `File ${file.id}` : 'Unnamed file');

const getFileKey = (file, index) =>
  file?.id !== undefined ? String(file.id) : `file-${index}`;

const isImageFile = file =>
  file?.type?.startsWith('image/') ||
  /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(file?.name || '');

const FileUploadViewer = () => {
  const [uploadForm, setUploadForm] = useState(defaultUploadForm);
  const [uploadValidated, setUploadValidated] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');

  const [searchDesc, setSearchDesc] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [previewingKey, setPreviewingKey] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [preview, setPreview] = useState(null);

  const handleUploadFormChange = event => {
    const { name, type, checked, value, files } = event.target;
    if (uploadError) setUploadError('');
    const selectedFile = files?.[0];
    if (type === 'file' && selectedFile && !isImageFile(selectedFile)) {
      event.target.value = '';
      setUploadError('Only image files are allowed.');
      setUploadForm(prev => ({ ...prev, file: null }));
      return;
    }

    setUploadForm(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'file'
          ? selectedFile ?? null
          : value
    }));
  };

  const handleKeywordsChange = keywords => {
    if (uploadError) setUploadError('');
    setUploadForm(prev => ({ ...prev, keywords }));
  };

  const handleUpload = async event => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false || uploadForm.keywords.length === 0) {
      event.stopPropagation();
      setUploadValidated(true);
      return;
    }
    try {
      setUploading(true);
      setUploadValidated(true);
      setUploadError('');
      setUploadMessage('');
      const formData = new FormData();
      formData.append('File', uploadForm.file);
      formData.append('Description', uploadForm.description.trim());
      formData.append('Keywords', uploadForm.keywords.join(','));
      formData.append('Active', String(uploadForm.active));
      await uploadFile(formData);
      setUploadMessage('Image uploaded successfully.');
      setSearchDesc(uploadForm.description.trim());
    } catch (err) {
      setUploadError('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const previewFile = async (file, key) => {
    try {
      setPreviewingKey(key);
      setPreviewError('');
      setPreview(null);
      let url = extractPresignedUrl(file);
      if (!url && file?.id !== undefined) {
        url = extractPresignedUrl(await getPresignedFile(file.id));
      }
      if (!url) throw new Error('No file URL returned.');
      setPreview({ key, name: getFileDisplayName(file), url });
    } catch (err) {
      if (err?.response?.status === 404) setPreviewError('File not found.');
      else setPreviewError('Unable to load file.');
    } finally {
      setPreviewingKey('');
    }
  };

  const handleSearch = async event => {
    event.preventDefault();
    const trimmedDesc = searchDesc.trim();
    if (!trimmedDesc) return;
    try {
      setSearching(true);
      setSearchError('');
      setPreviewError('');
      setPreview(null);
      setSearchResults([]);
      const results = normalizeSearchResults(
        await searchFilesByDesc(trimmedDesc)
      );
      setSearchResults(results);
      setHasSearched(true);
      if (results.length === 1) {
        await previewFile(results[0], getFileKey(results[0], 0));
      }
    } catch (err) {
      setHasSearched(true);
      if (err?.response?.status !== 404)
        setSearchError('Unable to search files.');
    } finally {
      setSearching(false);
    }
  };

  const showNoResults =
    hasSearched && !searching && !searchError && searchResults.length === 0;

  return (
    <Tabs defaultActiveKey="upload" id="file-upload-viewer-tabs" className="mb-3">
      <Tab eventKey="upload" title="Upload Image">
        <Card>
          <Card.Body>
            {uploadMessage ? (
              <Alert variant="success">{uploadMessage}</Alert>
            ) : null}
            {uploadError ? <Alert variant="danger">{uploadError}</Alert> : null}
            <Form
              noValidate
              validated={uploadValidated}
              onSubmit={handleUpload}
            >
              <Form.Group className="mb-3">
                <Form.Label htmlFor="filesTestFile">Image</Form.Label>
                <Form.Control
                  id="filesTestFile"
                  name="file"
                  type="file"
                  accept="image/*"
                  onChange={handleUploadFormChange}
                  disabled={uploading}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please choose an image to upload.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="filesTestDescription">
                  Description
                </Form.Label>
                <Form.Control
                  id="filesTestDescription"
                  name="description"
                  type="text"
                  value={uploadForm.description}
                  onChange={handleUploadFormChange}
                  placeholder="Enter a description"
                  disabled={uploading}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a description.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="filesTestKeywords">Keywords</Form.Label>
                <TagsInput
                  value={uploadForm.keywords}
                  onChange={handleKeywordsChange}
                  disabled={uploading}
                  addOnBlur
                  addOnPaste
                  onlyUnique
                  tagProps={{
                    className:
                      'react-tagsinput-tag bg-primary bg-gradient text-light shadow-sm badge py-2',
                    classNameRemove: 'react-tagsinput-remove'
                  }}
                  inputProps={{
                    id: 'filesTestKeywords',
                    className: 'react-tagsinput-input',
                    style: { width: '16rem' },
                    placeholder: 'Type a keyword and press Enter'
                  }}
                />
                {uploadValidated && uploadForm.keywords.length === 0 ? (
                  <div className="invalid-feedback" style={{ display: 'block' }}>
                    Please enter at least one keyword.
                  </div>
                ) : null}
              </Form.Group>
              <Form.Check
                id="filesTestActive"
                name="active"
                type="switch"
                label="Active"
                checked={uploadForm.active}
                onChange={handleUploadFormChange}
                disabled={uploading}
                className="mb-3"
              />
              <Button type="submit" variant="primary" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Tab>
      <Tab eventKey="view" title="View">
        <Card>
          <Card.Body>
            {searchError ? <Alert variant="danger">{searchError}</Alert> : null}
            <Form onSubmit={handleSearch}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="filesTestSearchDesc">Description</Form.Label>
                <Form.Control
                  id="filesTestSearchDesc"
                  name="searchDesc"
                  type="text"
                  value={searchDesc}
                  onChange={event => setSearchDesc(event.target.value)}
                  placeholder="Enter a description to search"
                  disabled={searching}
                />
              </Form.Group>
              <Button
                type="submit"
                variant="primary"
                disabled={searching || !searchDesc.trim()}
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </Form>
            {showNoResults ? (
              <div className="text-muted" style={{ marginTop: '1rem' }}>
                No files found.
              </div>
            ) : null}
            {searchResults.length > 1 ? (
              <Table
                responsive
                size="sm"
                className="fs--1"
                style={{ marginTop: '1rem', marginBottom: 0 }}
              >
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th style={{ width: '1%' }} />
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((file, index) => {
                    const key = getFileKey(file, index);
                    return (
                      <tr key={key}>
                        <td style={{ verticalAlign: 'middle' }}>
                          {getFileDisplayName(file)}
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          {file?.description || ''}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="sm"
                            variant={
                              preview?.key === key ? 'primary' : 'falcon-default'
                            }
                            disabled={Boolean(previewingKey)}
                            onClick={() => previewFile(file, key)}
                          >
                            {previewingKey === key ? 'Loading...' : 'Preview'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : null}
            {previewingKey && searchResults.length === 1 ? (
              <div className="text-muted" style={{ marginTop: '1rem' }}>
                Loading preview...
              </div>
            ) : null}
            {previewError ? (
              <Alert variant="danger" style={{ marginTop: '1rem' }}>
                {previewError}
              </Alert>
            ) : null}
            {preview ? (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  {preview.name}
                </div>
                <a href={preview.url} target="_blank" rel="noreferrer">
                  Open file in new tab
                </a>
                <div style={{ marginTop: '0.75rem' }}>
                  <img
                    src={preview.url}
                    alt={preview.name}
                    style={{ maxWidth: '100%', borderRadius: '0.375rem' }}
                  />
                </div>
              </div>
            ) : null}
          </Card.Body>
        </Card>
      </Tab>
    </Tabs>
  );
};

export default FileUploadViewer;
